import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { createPortal } from 'react-dom'
import posthog from 'posthog-js'

import DotsLoading from 'components/loadings/dotsLoading'
import CashboxHeader from 'modules/cashbox/header'
import CashboxActions from 'modules/cashbox/actions'
import CashboxTabs from 'modules/cashbox/tabs'
import { useCashbox } from 'modules/cashbox/hooks'
import { DEMO_CASHBOX_ID } from 'services/cashbox/demo'
import downloadMark from 'assets/images/landing/download.png'
import eitaaMark from 'assets/images/landing/eitaa.png'
import LandingSupportChat from 'modules/landing/supportChat'
import LandingTour from 'modules/landing/tour'
import { TOPICS, nextTopic, topicByKey } from 'modules/landing/topics'
import {
    normalizeLandingCode,
    rememberLandingAttribution,
} from 'modules/landing/code'
import { hasSession } from 'utilities/session'
import {
    APP_DOWNLOAD_URL,
    EITAA_MINIAPP_URL,
    introPath,
} from 'modules/landing/links'
import { getInitData } from 'modules/miniapp'
import { requestFundTransfer } from 'services/miniapp'
import TransferSheet from 'modules/landing/transfer'

/**
 * The قرعه‌کشی landing — `/q/<code>`.
 *
 * Reached two ways, both carrying the same per-recipient code: from the Eitaa
 * mini app (`?startapp=q_<code>`, resolved in `bootstrapMiniapp`) and from a
 * plain link shared anywhere else. A visitor here has no account and no box,
 * so the page has to be readable with no session at all.
 *
 * **It is written to someone who already runs a صندوق.** The campaign list is
 * people we know manage one, so the ask is not «بساز» — they have built one
 * already, and telling them to start over is telling them their fund does not
 * count. The ask is to move the one they have, and the page ends in a call
 * back from us rather than a sign-up form, because the migration is work we
 * do with them and not something they can finish alone on a phone.
 *
 * What it shows is the **real** صندوق قرعه‌کشی screen — the same header, the
 * same tabs, the same search — over a fixture box (`services/cashbox/demo`).
 * Reusing the product screen rather than drawing a marketing mock is the whole
 * point: the promise a landing makes is "this is what you get", and a mock
 * makes that promise unverifiable and lets the two drift apart.
 *
 * Nothing on it can write. Every control that would navigate into the app is
 * intercepted and turned into the call to action, so there is no path from
 * here to a mutation on a box that does not exist.
 */

export default function LotteryLandingPage() {
    const { code: codeParam } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()

    // `?c=` is accepted as well as the path segment so a link can be pasted
    // into places that mangle path segments (and so `/q?c=x` is not a dead end).
    const code = useMemo(
        () => normalizeLandingCode(codeParam ?? searchParams.get('c')),
        [codeParam, searchParams]
    )

    // Through the ordinary hook and the ordinary service call: the demo answer
    // is a guard inside `services/cashbox`, so this exercises the same path a
    // real box does, loading state included.
    const { cashbox, isLoading } = useCashbox(DEMO_CASHBOX_ID)

    // Which topic the reader asked about, straight from the URL — so a link
    // to one explanation is shareable, the back button steps between them, and
    // «کدام موضوع را باز کردند» is a question the funnel can answer.
    const topic = topicByKey(searchParams.get('t'))
    const [listOpen, setListOpen] = useState(false)
    const [ctaOpen, setCtaOpen] = useState(false)
    const [requested, setRequested] = useState(false)
    const [doneOpen, setDoneOpen] = useState(false)
    const [phoneOpen, setPhoneOpen] = useState(false)
    const arrivedAt = useRef(Date.now())
    const furthestStep = useRef(-1)
    const deepest = useRef(0)
    const topicOpenedAt = useRef(Date.now())

    const track = useCallback(
        (event: string, props: Record<string, unknown> = {}) => {
            posthog.capture(event, { landing_code: code, ...props })
        },
        [code]
    )

    // Arrival. Registered as well as captured: the code becomes a
    // super-property, so a login or a صندوق created days later still carries
    // which invitation brought this person in. That is the whole question the
    // campaign is asking.
    useEffect(() => {
        if (code) rememberLandingAttribution(code)
        posthog.capture('lottery_landing_viewed', {
            landing_code: code,
            has_code: !!code,
            authenticated: hasSession(),
        })
    }, [code])

    // Reported once per topic opened, not once per visit: the same person
    // reading three explanations is three data points about which parts of
    // Hamyan an invited manager actually cares about.
    useEffect(() => {
        if (!topic) return
        topicOpenedAt.current = Date.now()
        posthog.capture('lottery_topic_viewed', {
            landing_code: code,
            topic: topic.key,
        })
    }, [topic, code])

    // How far down the demo they got. On the intro this measures whether a
    // short hero earned the scroll; here it separates somebody who read the
    // صندوق from somebody who saw the header and left.
    useEffect(() => {
        const onScroll = () => {
            const doc = document.documentElement
            const max = doc.scrollHeight - window.innerHeight
            if (max <= 0) return
            const pct = Math.min(
                100,
                Math.round(((window.scrollY || doc.scrollTop) / max) * 100)
            )
            if (pct > deepest.current) deepest.current = pct
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    const openTopic = (key: string, from: string) => {
        track('lottery_topic_opened', { topic: key, from })
        setListOpen(false)
        setSearchParams({ t: key }, { replace: false })
    }

    const closeTour = (reason: string) => {
        // Seconds, so a topic that was read can be told from one that was
        // opened and dismissed. Without it every topic looks equally popular
        // and the tour cannot be edited on evidence.
        track('lottery_topic_closed', {
            topic: topic?.key,
            reason,
            seconds: Math.round((Date.now() - topicOpenedAt.current) / 1000),
        })
        setSearchParams({}, { replace: true })
    }

    // How long they stayed, and how far into the tour they got. Reported on the
    // way out rather than on a timer: a fixed "still here after 30s" ping says
    // much less than the actual dwell, and dwell is the strongest signal we
    // have for who was genuinely interested.
    useEffect(() => {
        const report = () => {
            posthog.capture('lottery_landing_left', {
                landing_code: code,
                seconds: Math.round((Date.now() - arrivedAt.current) / 1000),
                tour_furthest_step: furthestStep.current,
                scroll_depth: deepest.current,
            })
        }
        window.addEventListener('pagehide', report)
        return () => {
            window.removeEventListener('pagehide', report)
            report()
        }
    }, [code])

    /** Every in-app destination on this page ends here instead. */
    const intercept = (to: string) => {
        track('lottery_landing_action_tapped', { action: to })
        // Once the call is already booked, re-asking for it reads as if the
        // first tap did not register.
        if (requested) setDoneOpen(true)
        else setCtaOpen(true)
    }

    /** The request is on file and somebody will be asked to call. */
    const confirm = (via: string) => {
        track('lottery_landing_transfer_requested', { via })
        setPhoneOpen(false)
        setRequested(true)
        setDoneOpen(true)
    }

    /**
     * «صندوقم را منتقل کنید» — the only thing this page asks for.
     *
     * It navigates nowhere. Sending someone who already runs a fund to a
     * sign-up screen asks them to rebuild by hand what we are offering to move
     * for them, and the tail of that flow — members, past cycles, who has
     * already won — is not work anyone finishes alone on a phone. So the tap
     * books a call, and the call is where the migration happens.
     *
     * A signed-in visitor is never asked for a number: we hold theirs already,
     * the backend reads it off the session, and asking anyway would be asking
     * them to prove something they proved when they logged in. Everyone else
     * gets `TransferSheet`, which prefers the messenger's own signed contact
     * over anything typed.
     */
    const requestTransfer = async (from: string) => {
        const authenticated = hasSession()
        track('lottery_landing_cta_clicked', { from, authenticated })
        setCtaOpen(false)
        if (!authenticated) {
            setPhoneOpen(true)
            return
        }
        try {
            await requestFundTransfer({ code, initData: getInitData() })
            confirm('session')
        } catch {
            // Signed in and it still did not land — the session is stale, or
            // the network went. Fall through to the sheet rather than claiming
            // a call that was never booked.
            setPhoneOpen(true)
        }
    }

    if (isLoading || !cashbox) return <DotsLoading />

    return (
        <div className="bg-white min-h-screen pb-28">
            <div className="px-4 pt-4">
                <div className="rounded-card border border-[#CDE3BF] bg-[#F3F9EF] p-3 flex items-start gap-2">
                    <div className="flex-1">
                        <p className="text-sm font-bold text-[#3B7A25]">
                            این یک صندوق نمونه است
                        </p>
                        <p className="text-xs text-[#3B7A25] mt-1 leading-5">
                            اسم‌ها و مبلغ‌ها ساختگی‌اند. صندوق خودتان در همیان
                            دقیقاً همین شکلی می‌شود.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="shrink-0 h-8 px-3 rounded-[8px] border border-[#CDE3BF] bg-white text-xs font-bold text-[#3B7A25]"
                        onClick={() => {
                            track('lottery_topic_list_opened', {})
                            setListOpen(true)
                        }}
                    >
                        راهنما
                    </button>
                </div>
            </div>

            <LandingSupportChat code={code} surface="demo" />

            <div data-tour="summary">
                <CashboxHeader cashbox={cashbox} onNavigate={intercept} />
            </div>

            <div data-tour="actions">
                <CashboxActions cashbox={cashbox} onNavigate={intercept} />
            </div>

            <div data-tour="tabs">
                <CashboxTabs
                    id={DEMO_CASHBOX_ID}
                    cashbox={cashbox}
                    forceTab={topic?.tab}
                    onTabChange={(tab) =>
                        track('lottery_landing_tab_viewed', { tab })
                    }
                    onSearch={(chars, tab) =>
                        track('lottery_landing_searched', { chars, tab })
                    }
                />
            </div>

            <Entrances
                code={code}
                onOpen={(target, url) => {
                    track('lottery_landing_external_opened', { target })
                    window.open(url, '_blank', 'noopener,noreferrer')
                }}
            />

            <StickyCta
                requested={requested}
                onClick={() =>
                    requested ? setDoneOpen(true) : requestTransfer('sticky')
                }
                onSecondary={() => {
                    track('lottery_topic_list_opened', {})
                    setListOpen(true)
                }}
            />

            {topic && (
                <LandingTour
                    steps={[
                        {
                            key: topic.key,
                            anchor: topic.anchor,
                            title: topic.title,
                            body: topic.body,
                        },
                    ]}
                    open
                    primaryLabel={
                        nextTopic(topic.key) ? 'موضوع بعدی' : 'تمام شد'
                    }
                    secondaryLabel="فهرست موضوع‌ها"
                    onSkip={() => {
                        track('lottery_topic_closed', {
                            topic: topic.key,
                            reason: 'list',
                        })
                        setListOpen(true)
                        setSearchParams({}, { replace: true })
                    }}
                    onDone={() => {
                        const next = nextTopic(topic.key)
                        if (next) openTopic(next.key, 'tour_next')
                        else closeTour('finished')
                    }}
                />
            )}

            {listOpen && (
                <TopicSheet
                    current={topic?.key}
                    onPick={(key) => openTopic(key, 'sheet')}
                    onClose={() => setListOpen(false)}
                />
            )}

            {ctaOpen && (
                <CtaSheet
                    onClose={() => setCtaOpen(false)}
                    onTransfer={() => requestTransfer('sheet')}
                />
            )}

            {phoneOpen && (
                <TransferSheet
                    code={code}
                    onClose={() => {
                        setPhoneOpen(false)
                        track('lottery_landing_phone_abandoned', {})
                    }}
                    onDone={confirm}
                    onEvent={track}
                />
            )}

            {doneOpen && <DoneSheet onClose={() => setDoneOpen(false)} />}
        </div>
    )
}

/**
 * The call to action, pinned to the bottom of the viewport.
 *
 * Portalled into `<body>` rather than rendered in place: `FundLayout` wraps
 * every route in a framer-motion `x` animation, and `position: fixed` inside a
 * transformed ancestor anchors to that ancestor instead of the viewport — the
 * bar would scroll away with the page. This is the same trap the payment bars
 * hit.
 */
function StickyCta({
    requested,
    onClick,
    onSecondary,
}: {
    requested: boolean
    onClick: () => void
    onSecondary: () => void
}) {
    return createPortal(
        <div
            data-tour="cta"
            className="fixed inset-x-0 bottom-0 z-[900] bg-white border-t border-divider px-4 pt-3"
            style={{ paddingBottom: 'calc(12px + var(--safe-bottom))' }}
        >
            <button
                type="button"
                onClick={onClick}
                className={`w-full h-12 rounded-[10px] text-sm font-bold active:scale-[0.99] ${
                    requested
                        ? 'bg-[#F3F9EF] text-[#3B7A25] border border-[#CDE3BF]'
                        : 'bg-green-1 text-white'
                }`}
            >
                {requested
                    ? 'درخواستتان ثبت شد — با شما تماس می‌گیریم'
                    : 'صندوقم را به همیان منتقل کنید'}
            </button>
            <button
                type="button"
                onClick={onSecondary}
                className="w-full h-8 mt-1 text-xs text-text-secondary"
            >
                این صفحه را برایم توضیح بده
            </button>
        </div>,
        document.body
    )
}

/**
 * What a tap on one of the demo's own controls opens.
 *
 * The buttons are the pitch — seeing that قرعه‌کشی is one tap is most of the
 * argument — so they stay live rather than being greyed out, and the tap says
 * plainly why nothing happened and what to do instead.
 */
function CtaSheet({
    onClose,
    onTransfer,
}: {
    onClose: () => void
    onTransfer: () => void
}) {
    return createPortal(
        <div className="fixed inset-0 z-[950] flex items-end" role="dialog">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div
                className="relative w-full bg-white rounded-t-[16px] p-4"
                style={{ paddingBottom: 'calc(16px + var(--safe-bottom))' }}
            >
                <p className="text-base font-bold text-black">
                    این صندوق فقط نمونه است
                </p>
                <p className="text-sm text-text-secondary leading-6 mt-2">
                    روی صندوق خودتان این دکمه واقعاً کار می‌کند. صندوقی که الان
                    اداره می‌کنید را به همیان منتقل کنید تا همین‌جا قرعه بکشید و
                    پرداخت‌ها را ببینید.
                </p>
                <button
                    type="button"
                    onClick={onTransfer}
                    className="w-full h-12 mt-4 rounded-[10px] bg-green-1 text-white text-sm font-bold active:scale-[0.99]"
                >
                    صندوقم را منتقل کنید
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="w-full h-10 mt-1 text-sm text-text-secondary"
                >
                    بازگشت به صندوق نمونه
                </button>
            </div>
        </div>,
        document.body
    )
}

/**
 * The end of the page: we will call you.
 *
 * Shown only after the request is actually on file — `TransferSheet` resolves
 * when the backend has answered, not when the button was tapped. That ordering
 * is the whole reason this copy is allowed to promise a call: a confirmation
 * rendered optimistically would be a promise nobody had been asked to keep.
 */
function DoneSheet({ onClose }: { onClose: () => void }) {
    return createPortal(
        <div className="fixed inset-0 z-[950] flex items-end" role="dialog">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div
                className="relative w-full bg-white rounded-t-[16px] p-4"
                style={{ paddingBottom: 'calc(16px + var(--safe-bottom))' }}
            >
                <div className="w-12 h-12 rounded-full bg-[#F3F9EF] flex items-center justify-center mb-3">
                    <span className="text-2xl leading-none text-[#3B7A25]">
                        ✓
                    </span>
                </div>
                <p className="text-base font-bold text-black">
                    درخواستتان ثبت شد
                </p>
                <p className="text-sm text-text-secondary leading-6 mt-2">
                    همکاران ما در اسرع وقت <b>جهت انتقال صندوقت</b> باهات تماس
                    می‌گیرند — اعضا، سهم‌ها و نوبت‌هایی که تا حالا کشیده‌ای، همه
                    با هم منتقل می‌شوند.
                </p>
                <p className="text-xs text-text-secondary leading-5 mt-3">
                    تا آن موقع می‌توانی همین صندوق نمونه را بگردی.
                </p>
                <button
                    type="button"
                    onClick={onClose}
                    className="w-full h-12 mt-4 rounded-[10px] bg-green-1 text-white text-sm font-bold active:scale-[0.99]"
                >
                    باشه
                </button>
            </div>
        </div>,
        document.body
    )
}

/**
 * Ways off this page and into the real product.
 *
 * Sits at the very bottom, below the fund: somebody who scrolled this far has
 * read the whole sample and is the only person for whom «اپ را بگیر» is an
 * invitation rather than an interruption.
 */
function Entrances({
    code,
    onOpen,
}: {
    code: string | null
    onOpen: (target: string, url: string) => void
}) {
    return (
        <div className="mx-auto w-full max-w-[560px] px-4 pb-4 pt-2">
            <div className="rounded-card border border-divider bg-white p-4">
                <p className="text-sm font-bold text-black">
                    همیان را روی دستگاه خودت باز کن
                </p>
                <p className="mt-1 text-xs leading-6 text-text-secondary">
                    این صندوق نمونه بود. نسخهٔ واقعی همین شکلی است — روی گوشی،
                    مرورگر یا داخل ایتا.
                </p>
                <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    <button
                        type="button"
                        onClick={() => onOpen('app_download', APP_DOWNLOAD_URL)}
                        className="flex h-14 items-center justify-center gap-2 rounded-[10px] border border-divider bg-white leading-tight active:scale-[0.99]"
                    >
                        <img
                            src={downloadMark}
                            alt=""
                            aria-hidden
                            className="h-7 w-7 shrink-0"
                        />
                        <span className="text-right leading-tight">
                            <span className="block text-sm font-bold text-black">
                                دریافت اپلیکیشن
                            </span>
                            <span className="block text-[11px] text-text-secondary">
                                بازار، مایکت، گوگل‌پلی
                            </span>
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() =>
                            onOpen('eitaa_miniapp', EITAA_MINIAPP_URL)
                        }
                        className="flex h-14 items-center justify-center gap-2 rounded-[10px] border border-divider bg-white text-sm font-bold text-black active:scale-[0.99]"
                    >
                        <img
                            src={eitaaMark}
                            alt=""
                            aria-hidden
                            className="h-7 w-7 shrink-0"
                        />
                        باز کردن در ایتا
                    </button>
                </div>
                <Link
                    to={introPath(code)}
                    className="mt-3 block text-center text-xs text-text-secondary"
                >
                    برگشت به توضیح همیان
                </Link>
            </div>
        </div>
    )
}

/**
 * The list of explanations, reachable from inside the demo.
 *
 * The intro has the same list, but somebody who is already three taps into the
 * fund should not have to go back to a marketing page to find the next one —
 * and «راهنما» that reopens a fixed tour from step one is worse than no button
 * at all for a reader who only wanted the part about اقساط.
 */
function TopicSheet({
    current,
    onPick,
    onClose,
}: {
    current?: string
    onPick: (key: string) => void
    onClose: () => void
}) {
    return createPortal(
        <div className="fixed inset-0 z-[950] flex items-end" role="dialog">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div
                className="relative max-h-[85dvh] w-full overflow-y-auto rounded-t-[16px] bg-white p-4"
                style={{ paddingBottom: 'calc(16px + var(--safe-bottom))' }}
            >
                <p className="text-base font-bold text-black">
                    چه چیزی را نشانتان بدهم؟
                </p>
                <div className="mt-3 overflow-hidden rounded-card border border-divider">
                    {TOPICS.map((item, index) => (
                        <button
                            key={item.key}
                            type="button"
                            onClick={() => onPick(item.key)}
                            className={`flex w-full items-center gap-3 px-3.5 py-3 text-right active:bg-[#FAFAFA] ${
                                index ? 'border-t border-divider' : ''
                            } ${item.key === current ? 'bg-brand-soft' : ''}`}
                        >
                            <span
                                aria-hidden
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-brand-soft text-base leading-none"
                            >
                                {item.icon}
                            </span>
                            <span className="min-w-0 flex-1 text-sm font-bold text-black">
                                {item.title}
                            </span>
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="mt-3 h-11 w-full text-sm text-text-secondary"
                >
                    خودم می‌گردم
                </button>
            </div>
        </div>,
        document.body
    )
}
